import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft, BookOpen, Calculator, FlaskConical, BookMarked, Mic, Volume2,
  Sparkles, Trophy, Users, Shield, ShieldCheck, Brain, Award
} from 'lucide-react';
import SEO from '@/components/SEO';
import ZazuLogo from '@/components/ZazuLogo';
import SocialFooterLinks from '@/components/SocialFooterLinks';

const featureGroups = [
  {
    title: 'Core subjects, one app',
    icon: BookOpen,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    items: [
      { icon: BookOpen, title: 'Reading & Phonics', desc: 'Letter sounds, sight words, CVC and blends — sequenced so kids read real sentences without frustration.' },
      { icon: Calculator, title: 'Math', desc: 'Number sense, counting, addition, subtraction. Aligned to Common Core so kids stay on track.' },
      { icon: FlaskConical, title: 'Science', desc: 'NGSS-aligned exploration — plants, animals, weather, and the world around us.' },
      { icon: BookMarked, title: 'Bible & Faith', desc: '52-week scripture-based curriculum with stories, memory verses, and worship songs (Premium or standalone).' },
    ]
  },
  {
    title: 'How Zazu teaches',
    icon: Brain,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    items: [
      { icon: Brain, title: 'AI-adaptive learning', desc: 'Difficulty adjusts to your child\'s answers. Weak spots get more practice — strong spots move forward.' },
      { icon: Mic, title: 'Voice input', desc: 'Pre-readers can answer by talking. Speech-to-text turns words into progress.' },
      { icon: Volume2, title: 'Text-to-speech + karaoke', desc: 'Read-along mode with word-by-word highlighting. Speed toggle for beginners (tortoise) or fluent readers (hare).' },
      { icon: Trophy, title: 'Gamification', desc: 'Badges, streaks, XP, and a trophy room. Kids stay engaged without dark-pattern manipulation.' },
    ]
  },
  {
    title: 'Built for families',
    icon: Users,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    items: [
      { icon: Users, title: 'Up to unlimited profiles', desc: 'Family plan: 5 kids. Premium: unlimited. Each child gets their own curriculum path.' },
      { icon: Award, title: 'Parent dashboard', desc: 'Weekly reports, PDF exports, and clear "what your child learned this week" recaps.' },
      { icon: Shield, title: 'COPPA-compliant', desc: 'No data sold. No ads shown to kids. Parents can export or delete everything, anytime.' },
      { icon: ShieldCheck, title: 'Ad-free, offline access', desc: 'Family and Premium tiers work offline for road trips and Wi-Fi-free time.' },
    ]
  },
];

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-teal-50/40">
      <SEO
        title="Zazu Features — Phonics, Math, Bible, AI-Adaptive Learning for Kids 3-10"
        description="Every feature Zazu ships: reading and phonics, math, science, Bible curriculum, AI-adaptive learning, voice input, text-to-speech karaoke, parent dashboard, offline mode, COPPA-compliant."
        path="/features"
      />

      <header className="p-4 border-b border-slate-100 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-5 h-5" />
            <ZazuLogo size="sm" />
          </Link>
          <div className="flex gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-teal-700 hover:bg-teal-800">Start free</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Everything included</span>
          </div>
          <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="features-h1">
            Every subject. Every kid. One app.
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Phonics, math, science, and scripture — with AI that adapts to your child and a parent dashboard that shows what they actually learned this week.
          </p>
        </div>

        {featureGroups.map((group, gi) => (
          <section key={group.title} className="mb-16" data-testid={`features-group-${gi}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-xl ${group.bg} flex items-center justify-center`}>
                <group.icon className={`w-6 h-6 ${group.color}`} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{group.title}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {group.items.map((item) => (
                <Card key={item.title} className="border-slate-200 hover:border-emerald-300 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      <div className={`w-10 h-10 rounded-lg ${group.bg} flex items-center justify-center flex-shrink-0`}>
                        <item.icon className={`w-5 h-5 ${group.color}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ))}

        <div className="text-center bg-white rounded-2xl border-2 border-emerald-100 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Try every feature — free forever</h2>
          <p className="text-slate-600 mb-6">No credit card. Cancel any paid plan within 30 days for a full refund.</p>
          <Link to="/register">
            <Button size="lg" className="bg-teal-700 hover:bg-teal-800 h-14 px-8 rounded-full" data-testid="features-cta-btn">
              Start free
            </Button>
          </Link>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-10 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <ZazuLogo size="md" className="[&_span]:text-white" />
          </div>
          <p className="text-sm mb-3">Impacting lives, one syllable at a time.</p>
          <SocialFooterLinks />
          <p className="text-xs mt-4">&copy; {new Date().getFullYear()} Zazu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
